/* eslint-disable complexity */
import { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { useCrudActivityContext } from '@futrli/shared-services';
import { useFinancialConfig } from '@futrli/views/hooks/useFinancialConfig';

import { useLoadChartOfAccounts } from '../useLoadChartOfAccounts/useLoadChartOfAccounts';
import { useLoadLineData } from '../useLoadLineData/useLoadLineData';
import { useLoadPaymentData } from '../useLoadPaymentData/useLoadPaymentData';
import { useLoadPredictionData } from '../useLoadPredictionData/useLoadPredictionData';
import { usePredictionsInfoContext } from '../../../detail/context/predictions-info-context';
import { useRequestErrorMessages } from '../useRequestErrorMessages/useRequestErrorMessages';

/**
 * Handles loading all of the data needed for the prediction builder
 */
export const useLoadPredictionBuilderData = (urlPredictionId?: string, urlPredictionType?: string) => {
  const { forecast, fetchForecastReport, fetchForecast } = usePredictionsInfoContext();
  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisationId || '';
  const { addReading, removeReading } = useCrudActivityContext();

  const {
    data: chartOfAccounts,
    isLoadingOrInit: isLoadingChartOfAccounts,
    error: chartOfAccountsError
  } = useLoadChartOfAccounts(workspaceId, organisationId, forecast?.id);

  const {
    financialConfig,
    isLoading: isLoadingFinancialConfig,
    error: financialConfigError
  } = useFinancialConfig(
    workspaceId && organisationId ? { workspaceId, entityType: 'organisation', entityId: organisationId } : null
  );

  const {
    data: lineData,
    isLoadingOrInit: isLoadingLineData,
    reload: reloadLineData,
    error: lineDataError
  } = useLoadLineData(workspaceId, organisationId, forecast?.id, financialConfig);

  const {
    data: predictionData,
    isLoadingOrInit: isLoadingPredictionData,
    error: predictionDataError,
    hasAllParams: hasAllPredictionDataParams
  } = useLoadPredictionData(workspaceId, organisationId, forecast?.id, urlPredictionId, urlPredictionType);

  const {
    data: paymentData,
    isLoadingOrInit: isLoadingPaymentData,
    error: paymentDataError,
    hasAllParams: hasAllPaymentDataParams
  } = useLoadPaymentData(
    workspaceId,
    organisationId,
    forecast?.id,
    urlPredictionId,
    predictionData?.payment_type?.id,
    predictionData?.payment_type?.type
  );

  useRequestErrorMessages(chartOfAccountsError);
  useRequestErrorMessages(financialConfigError);
  useRequestErrorMessages(lineDataError);
  useRequestErrorMessages(predictionDataError);
  useRequestErrorMessages(paymentDataError);

  const isLoading =
    isLoadingChartOfAccounts ||
    isLoadingFinancialConfig ||
    isLoadingLineData ||
    (hasAllPredictionDataParams && isLoadingPredictionData) ||
    (hasAllPaymentDataParams && isLoadingPaymentData);

  const reloadForecastData = useCallback(() => {
    fetchForecastReport!('all');
    fetchForecast!();
  }, [fetchForecast, fetchForecastReport]);

  // Update CRUD flags when reading data
  useEffect(() => {
    isLoading ? addReading('predictionBuilderData') : removeReading('predictionBuilderData');
  }, [addReading, isLoading, removeReading]);

  return {
    chartOfAccounts,
    financialConfig,
    isLoading,
    lineData,
    paymentData,
    predictionData,
    reloadForecastData,
    reloadLineData
  };
};
